import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import GetState from '../../../GetState';
import { QuestRoute } from '../../../api/QuestRoutes';
import { generateId, shortHandDateTime } from '../../../utility/functions';
import { IsAuthorized, userRoles } from '../../../RoleProtected';
import Table, { Ctd } from '../../../table/Table';
import Image from '../../../Image';
import Button from '../../../forms/Button';
import LinkTo, { buildAccountURL } from '../../../forms/LinkTo';
import ManagementRow from '../../../table/ManagementRow';
import Col from '../../../layout/Col';
import Flag, { translateFlag } from '../../plateReads/Flag';
import usePlateReadActions from '../../plateReads/usePlateReadActions';
import { renderCustomerLink } from '../../plateReads/LicensePlateReads';

const multiple = 'Multiple';

// Link to a customer's plate reads with the Alerts filter already on.
// Only app users have a plate reads tab, other account types fall back to their account page.
export const customerPlateReadsUrl = (customer) => {
    const url = buildAccountURL(null, customer.customerId, customer.loyaltyAccountId);
    if (!url) return false;
    return url.startsWith('/user/') ? `${url}?alerts=true#PlateReads` : url;
}

// Flatten a customer with their alert plate reads into a single report row
export const toRow = (customer) => {
    const alerts = customer.alerts ?? [];
    const flagCounts = {};
    alerts.forEach(a => a.flags.forEach(flag => flagCounts[flag] = (flagCounts[flag] ?? 0) + 1));
    const alertCount = Object.values(flagCounts).reduce((a, b) => a + b, 0);
    const [first] = alerts;
    const manyReads = alerts.length > 1;

    return {
        ...customer,
        flagCounts,
        alertCount,
        singleRead: manyReads ? null : first,
        alertsText: Object.entries(flagCounts).map(([flag, count]) => `${translateFlag(flag)} (${count})`).join(', '),
        plate: manyReads ? multiple : first?.plate,
        location: manyReads ? multiple : first?.location,
        alertDate: manyReads ? multiple : shortHandDateTime(first?.dateRead),
        lastAlertDate: alerts.reduce((latest, a) => !latest || a.dateRead > latest ? a.dateRead : latest, null),
        // set when exactly one alert exists, so the full set of row actions applies
        singleAlert: alertCount === 1 ? first : null,
    };
}

const ClubShield = ({ ...props }) => {
    const navigate = useNavigate();
    const canViewCustomers = IsAuthorized(userRoles.viewCustomers);

    const [data, refreshData] = GetState(QuestRoute.plateReads.clubShield);
    const { renderActionButtons } = usePlateReadActions(() => refreshData());

    const rows = useMemo(() => data instanceof Array ? data.map(toRow) : data, [data]);

    const renderActions = (e) => {
        if (e.singleAlert) {
            const { singleAlert: a } = e;
            return renderActionButtons({
                customerId: e.customerId,
                customerName: e.customerName,
                plate: a.plate,
                plateReadId: a.plateReadId,
                transRecordId: a.transRecordId,
                flags: a.flags,
            });
        }

        return [
            <Button
                key={generateId()}
                icon='camera'
                label='Go to customer plate reads'
                onClick={() => navigate(customerPlateReadsUrl(e))}
            />
        ];
    }

    const tableConfig = {
        tableId: 'clubShield',
        data: rows,
        headers: [
            {
                label: 'Customer Name',
                propKey: 'customerName',
            }, {
                label: 'Alerts',
                propKey: 'alertsText',
            }, {
                label: 'Plate',
                propKey: 'plate',
            }, {
                label: 'Location',
                propKey: 'location',
                options: true,
            }, {
                label: 'Lifetime App Scans',
                propKey: 'lifetimeAppScans',
                type: 'number',
            }, {
                label: 'Transactions',
            }, {
                label: 'Alert Date',
                propKey: 'alertDate',
            },
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={renderActions(e)}
                layout='elipsis'
            >
                <td>{renderCustomerLink(e, canViewCustomers)}</td>
                <td>
                    <Col>
                        {Object.entries(e.flagCounts).map(([flag, count]) => (
                            <span key={flag} style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                <Flag flag={flag} /> {translateFlag(flag)} ({count})
                            </span>
                        ))}
                    </Col>
                </td>
                {e.singleRead ? (
                    <Ctd>
                        <Image
                            src={'data:image/gif;base64,' + e.singleRead.smallPlateImage}
                            modalTitle={e.plate}
                            alt='Car'
                        />
                    </Ctd>
                ) : (
                    <td>{multiple}</td>
                )}
                <td>{e.location}</td>
                <td>{e.lifetimeAppScans}</td>
                <td>
                    {canViewCustomers && customerPlateReadsUrl(e)
                        ? <LinkTo target='_blank' to={customerPlateReadsUrl(e)}>View</LinkTo>
                        : <span>View</span>}
                </td>
                <td>{e.alertDate}</td>
            </ManagementRow>
        )
    };

    return (
        <Table config={tableConfig} />
    );
}

export default ClubShield;
